import * as React from 'react';

import Divider from 'components/Divider';
import Glyph from 'components/Glyph';
import { GlyphName } from '@remind/r101-glyphs';
import { IconName } from 'components/Icon';
import Menu from 'components/Menu';
import MenuButton from 'components/MenuButtonDeprecated';
import MenuItem from 'components/MenuItem';
import TextWithGlyph from 'components/TextWithGlyph';
import TextWithIcon from 'components/TextWithIcon';
import { pick } from 'lodash';

type ComposableTableMenuItem<T> = {
  conditionToCheck?: keyof T;
  label: string;
  glyph?: GlyphName;
  icon?: IconName;
  fieldsForAction?: string[];
  onClickAction: Function;
  divided?: boolean;
};

type Props<T> = {
  data: T;
  menuItems: ComposableTableMenuItem<T>[];
};

function MenuButtonCell<T>({ data, menuItems }: Props<T>) {
  const items = menuItems
    .map(item => {
      if (item.conditionToCheck && !data[item.conditionToCheck]) {
        return null;
      }
      const fields = item.fieldsForAction ? pick(data, item.fieldsForAction) : undefined;

      return (
        <React.Fragment key={item.label}>
          {item.divided && (
            <MenuItem menuKey="divider">
              <Divider size="flush" />
            </MenuItem>
          )}
          <MenuItem
            menuKey={`menu-action-${item.label}`}
            data-test={`menu-button-cell-${item.label}`}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              item.onClickAction(fields);
            }}>
            {(item.glyph && <TextWithGlyph glyph={item.glyph}>{item.label}</TextWithGlyph>) ||
              (item.icon && <TextWithIcon icon={item.icon}>{item.label}</TextWithIcon>)}
          </MenuItem>
        </React.Fragment>
      );
    })
    .filter(i => i);

  if (items.length === 0) {
    return null;
  }

  const menu = <Menu>{items}</Menu>;
  return (
    <MenuButton
      onClick={e => e.stopPropagation()}
      menu={menu}
      aria-label="More actions"
      level="icon"
      color="mascot">
      <Glyph name="ellipses" size="medium" />
    </MenuButton>
  );
}

export function getMenuButtonCellGenerator<T>(menuItems: ComposableTableMenuItem<T>[]) {
  return (data: T) => {
    return <MenuButtonCell data={data} menuItems={menuItems} />;
  };
}
